/**
 * The topic **focus** vocabulary (NM-27).
 *
 * Focus replaces the old free-text guidance textarea (NEWS-80) with a fixed,
 * multi-select set of "angle" chips. A user ticks the angles they want from a
 * topic; the check prompt compiles the selection into instruction lines
 * (`buildContextLines` in `src/ai/prompt.ts`), and the sidebar row shows the
 * chosen labels under the topic name.
 *
 * **This is the single source of truth for both the server and the client** —
 * the same reason `src/categories.ts` is shared: the editor renders these chips,
 * the API validates writes against these ids, and the prompt compiler reads
 * these labels. A pure module with no `db/` import, so the browser bundle can
 * import it without pulling in the pglite/WASM engine.
 *
 * Ids, not labels, are what is stored (`Topic.focus`), so rewording a chip can
 * never orphan a topic that ticked it — the same call the profiles and category
 * slugs make. Stored ids are read **leniently**: an id no longer in this list is
 * dropped at use time, never a load failure (see `TopicSchema.focus`). Only
 * *writes* are constrained to known ids (the API rejects an unknown one).
 */

import { z } from 'zod';

export interface FocusOption {
  readonly id: string;
  readonly label: string;
}

/**
 * The focus angles, in the order they are offered in the editor and rendered on
 * the sidebar row. The first four are *angles* — a topic ticking any of them is
 * narrowed to those angles. `official` and `no-opinion` are *filters* on the
 * kind of source and writing, compiled into their own instruction lines.
 */
export const FOCUS_OPTIONS = [
  { id: 'regulatory', label: 'Regulatory & safety' },
  { id: 'business', label: 'Business & markets' },
  { id: 'product', label: 'Product & launches' },
  { id: 'research', label: 'Research & analysis' },
  { id: 'official', label: 'Official sources only' },
  { id: 'no-opinion', label: 'Exclude opinion & rumor' },
] as const satisfies readonly FocusOption[];

/**
 * The angle-selecting ids (a subset of {@link FOCUS_OPTIONS}). Ticking any of
 * these narrows the topic to that angle; the prompt names them together in one
 * "cover only these angles" line.
 */
export const ANGLE_FOCUS_IDS: readonly string[] = ['regulatory', 'business', 'product', 'research'];

const FOCUS_LABEL: ReadonlyMap<string, string> = new Map(FOCUS_OPTIONS.map((o) => [o.id, o.label]));

/** The human label for a focus id, or undefined when the id is not in the vocabulary. */
export function focusLabel(id: string): string | undefined {
  return FOCUS_LABEL.get(id);
}

/** Whether `id` is a known focus id — the check the API uses to reject a write. */
export function isKnownFocusId(id: string): boolean {
  return FOCUS_LABEL.has(id);
}

/**
 * Parse a set of focus ids at a trust boundary (the edit dialog's submit), keeping
 * only ids in the vocabulary. Filtering rather than rejecting so a stray value can
 * never block a save — the server does the authoritative rejection of a write.
 */
export const FocusIdsSchema = z.array(z.string()).transform((ids) => ids.filter(isKnownFocusId));

/**
 * The labels for `ids`, filtered to known ones and returned in vocabulary order
 * regardless of the order the ids arrived in — so the sidebar row reads the same
 * whatever order the checkboxes were ticked.
 */
export function selectedFocusLabels(ids: readonly string[]): string[] {
  const set = new Set(ids);
  return FOCUS_OPTIONS.filter((o) => set.has(o.id)).map((o) => o.label);
}
